import AlertRules, { AlertRuleThreshold } from '../types/Alerts/AlertRules';
import { AlertRuleFormData, AlertRuleFormValidation, EVALUATION_WINDOW_MAX } from '../types/Alerts/AlertRuleFormState';

export class AlertRuleFormValidator {

    static validateName(
        name: string,
        currentId: string,
        alertRules: AlertRules[]
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedName = name.trim();

        if (trimmedName === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        const duplicateIndex = alertRules.findIndex(
            rule => parseInt(rule.id) !== Number(currentId) &&
            rule.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (duplicateIndex !== -1) {
            return {
                isValid: false,
                isUsed: true,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validateThresholdArray(
        thresholdarray: AlertRuleThreshold[],
        units: string,
        decreasing: string
    ): { isValid: boolean; message?: string } {
        const filled = (thresholdarray ?? []).filter(t =>
            t.threshold !== null && t.threshold !== undefined && !isNaN(t.threshold)
        );
        if (filled.length === 0) {
            return { isValid: false, message: 'At least one threshold is required' };
        }

        const isPercent = units === '%';

        for (const t of filled) {
            if (t.threshold < 0) {
                return { isValid: false, message: 'Thresholds must be positive numbers' };
            }
            if (isPercent && t.threshold > 100) {
                return { isValid: false, message: "Thresholds can't be more than 100%" };
            }
            if (![1, 2, 3].includes(t.severity)) {
                return { isValid: false, message: 'Severity must be 1, 2 or 3' };
            }
        }

        const sorted = [...filled].sort((a, b) => b.severity - a.severity);
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1].threshold;
            const curr = sorted[i].threshold;
            if (decreasing === 'true') {
                if (curr >= prev) {
                    return { isValid: false, message: 'For decreasing metrics, lower severity must have a higher threshold' };
                }
            } else {
                if (curr <= prev) {
                    return { isValid: false, message: 'For increasing metrics, higher severity must have a higher threshold' };
                }
            }
        }

        return { isValid: true, message: undefined };
    }

    static validateEvaluationWindow(
        minutes: number
    ): { isValid: boolean; message?: string } {
        if (minutes === null || minutes === undefined || isNaN(minutes)) {
            return { isValid: false, message: 'Please fill in this field' };
        }
        if (minutes < 1) {
            return { isValid: false, message: 'Must be at least 1 minute' };
        }
        if (minutes > EVALUATION_WINDOW_MAX) {
            return { isValid: false, message: `Must be ${EVALUATION_WINDOW_MAX} minutes or less` };
        }
        return { isValid: true, message: undefined };
    }

    static validateStatistic(
        statistic: string
    ): { isValid: boolean; hasError: boolean } {
        if (!statistic || statistic.trim() === '') {
            return { isValid: false, hasError: true };
        }
        return { isValid: true, hasError: false };
    }

    static validateCompleteForm(
        formData: AlertRuleFormData,
        alertRules: AlertRules[]
    ): {
        isValid: boolean;
        validation: AlertRuleFormValidation;
    } {
        let isValid = true;
        // Condition section is hidden for *_status alert types — skip
        // metric/threshold validation when the user can't see/edit them.
        const conditionVisible = formData.metrictype !== 'status';

        const nameValidation = this.validateName(formData.name, formData.id, alertRules);
        if (!nameValidation.isValid) isValid = false;

        const thresholdValidation = conditionVisible
            ? this.validateThresholdArray(formData.thresholdarray, formData.units, formData.decreasing)
            : { isValid: true, message: undefined };
        if (!thresholdValidation.isValid) isValid = false;

        const evalWindowValidation = this.validateEvaluationWindow(formData.evaluationwindowminutes);
        if (!evalWindowValidation.isValid) isValid = false;

        const statisticValidation = conditionVisible
            ? this.validateStatistic(formData.statistic)
            : { isValid: true, hasError: false };
        if (!statisticValidation.isValid) isValid = false;

        const validation: AlertRuleFormValidation = {
            name: nameValidation,
            thresholdarray: thresholdValidation,
            evaluationwindow: evalWindowValidation,
            statistic: statisticValidation,
            isFormValid: isValid
        };

        return { isValid, validation };
    }
}

export class AlertRuleFormUtils {

    static getEntityOrMetric(key: string, typeValue: string): string {
        const typeArray = typeValue.split('_');
        return typeArray[key === 'entity' ? 0 : 1];
    }

    static buildTypeString(entityType: string, metricType: string): string {
        return `${entityType}_${metricType}`;
    }
}
